interface IRgbaUtil {
    [key: string]: number
}

type ConvertColorOptions = {
    format?: 'rgba' | 'hex'
    opacity?: number
}

export const hexToRgba = (hexColor: string, opacity: number): string => {
    const removeHash = (hex: string): string => (hex.charAt(0) === '#' ? hex.slice(1) : hex)
    const hexToDecimal = (hexDigit: string): number => parseInt(hexDigit, 16)

    const parseHex = (hexWithoutHash: string): IRgbaUtil => {
        const isShortedLengths: number[] = [3, 4]
        const isShort: boolean = isShortedLengths.includes(hexWithoutHash.length)

        const getHexDecimal = (from: number, to: number): number => {
            let hexDigitString: string = hexWithoutHash.slice(from * 2, to * 2)

            if (isShort) {
                hexDigitString = hexWithoutHash.slice(from, to).repeat(2)
            }

            return hexToDecimal(hexDigitString)
        }

        return {
            r: getHexDecimal(0, 1),
            g: getHexDecimal(1, 2),
            b: getHexDecimal(2, 3),
        }
    }

    const formatRgb = (decimalObject: IRgbaUtil, opacity: number): string => {
        const { r, g, b }: IRgbaUtil = decimalObject

        return `rgba(${r}, ${g}, ${b}, ${opacity})`
    }

    const convertHex = (hex: string, a: number): string => {
        const hashlessHex: string = removeHash(hex)
        const hexObject: IRgbaUtil = parseHex(hashlessHex)

        return formatRgb(hexObject, a)
    }

    return convertHex(hexColor, opacity)
}

const toSolidHex = (color: string): string => {
    const hex = color.trim()

    if (!hex.startsWith('#') || hex.length !== 9) {
        return hex
    }

    const channel = (from: number) => parseInt(hex.slice(from, from + 2), 16)
    const alpha = channel(7) / 255
    const blend = (value: number) =>
        Math.round(value * alpha + 255 * (1 - alpha))
            .toString(16)
            .padStart(2, '0')

    return `#${blend(channel(1))}${blend(channel(3))}${blend(channel(5))}`
}

/**
 * convertColor('#286cf2', 0.5) → 'rgba(40, 108, 242, 0.5)'
 * convertColor('#286cf2e0', { format: 'hex' }) → '#427ef4'
 */
export const convertColor = (color: string, opacityOrOptions: number | ConvertColorOptions = 1): string => {
    if (typeof opacityOrOptions === 'number') {
        return hexToRgba(color, opacityOrOptions)
    }

    if (opacityOrOptions.format === 'hex') {
        return toSolidHex(color)
    }

    return hexToRgba(color, opacityOrOptions.opacity ?? 1)
}
